import {addMinutes, differenceInDays, parseISO} from 'date-fns'
import {isEqual as _isEqual, isEmpty as _isEmpty} from 'lodash-es'
import sha256 from 'crypto-js/sha256.js'
import {Buffer} from 'buffer'

export const dateReviver = (key, value) => {
    let a
    if (typeof value === 'string') {
        a = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)Z$/.exec(value)
        if (a) {
            return new Date(Date.UTC(+a[1], +a[2] - 1, +a[3], +a[4], +a[5], +a[6]))
        }
    }
    return value
}

const matches = (obj, source) =>
    Object.keys(source).length === Object.keys(source).length &&
    // eslint-disable-next-line no-prototype-builtins
    Object.keys(source).every(key => obj.hasOwnProperty(key) && obj[key] === source[key])

function clone(obj) {
    return JSON.parse(JSON.stringify(obj), dateReviver)
}

function hash(obj) {
    const str = JSON.stringify(obj)
    return hashStr(str)
}

function hashStr(str) {
    return sha256(str)
}

function interopDefault(promise) {
    return promise.then(m => m.default || m)
}

function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

const later = (delay, value) => {
    let timer = 0
    let reject = null
    const promise = new Promise((resolve, _reject) => {
        reject = _reject
        timer = setTimeout(resolve, delay, value) as never
    })
    return {
        get promise() {
            return promise
        },
        cancel() {
            if (timer) {
                clearTimeout(timer)
                timer = 0
                reject()
                reject = null
            }
        },
    }
}

function clean(obj) {
    const propNames = Object.getOwnPropertyNames(obj)
    for (let i = 0; i < propNames.length; i++) {
        let propName = propNames[i]
        if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete obj[propName]
        }
    }
    return obj
}

function formatAxiosError(error) {
    const {url, method, params, headers, data} = error.config
    let errorResponse
    if (error.response) {
        if (error.response.data instanceof ArrayBuffer) {
            errorResponse = JSON.parse(Buffer.from(error.response.data).toString('utf8'))
        } else {
            errorResponse = typeof error.response.data === 'object' ? error.response.data : {msg: error.response.data}
        }
        errorResponse.status = error.response.status
    } else {
        errorResponse = {
            msg: error.message,
            status: error.code === 'ECONNABORTED' ? 504 : 500,
        }
    }
    errorResponse.request = {
        url,
        method,
        params,
        headers,
    }
    if (data) {
        errorResponse.request.data = data
    }
    return errorResponse
}

function downloadData(data, filename) {
    const blob = new Blob([data]),
        link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = filename
    link.click()
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (document.e2e) document.e2e.documentLink = link
}

async function downloadDataAsync(data, filename) {
    return await fetch(data)
        .then(res => res.blob())
        .then(blob => {
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = filename
            link.click()
        })
}

function getOfferAirportName({airportCode, airportDescription: {airportName, cityName, countryName}}) {
    return `${airportName}, ${cityName}, ${countryName} (${airportCode})`
}

const chunkArray = (array, chunk_size) =>
    Array(Math.ceil(array.length / chunk_size))
        .fill(null)
        .map((_, index) => index * chunk_size)
        .map(begin => array.slice(begin, begin + chunk_size))

const flipArray = arr => {
    const length = arr.length
    const result = {}
    for (let i = 0; i < length; i++) {
        result[arr[i]] = i
    }
    return result
}

const queryMapper = {
    cityCode: 'cC',
    hotelCode: 'hC',
    supplierCode: 'sC',
}

const alphabetMap = {
    a: '1',
    b: '2',
    c: '3',
    d: '4',
    e: '5',
    f: '6',
    g: '7',
    h: '8',
    i: '9',
    j: '1a',
    k: '2a',
    l: '3a',
    m: '4a',
    n: '5a',
    o: '6a',
    p: '7a',
    q: '8a',
    r: '9a',
    s: '1b',
    t: '2b',
    u: '3b',
    v: '4b',
    w: '5b',
    x: '6b',
    y: '7b',
    z: '8b',
    0: '1c',
    1: '2c',
    2: '3c',
    3: '4c',
    4: '5c',
    5: '6c',
    6: '7c',
    7: '8c',
    8: '9c',
    9: '1d',
}

function encodeSupplierCode(supplierCode) {
    let hash = '',
        index
    const length = supplierCode.length

    for (index = 0; index < length; index++) {
        if (supplierCode[index] === '.') {
            hash += '9b'
        } else {
            hash += alphabetMap[supplierCode[index]]
        }
    }

    return hash
}

function decodeSupplierCode(supplierCodeHash) {
    let supplierCode = '',
        index,
        symbol
    const length = supplierCodeHash.length
    for (index = 0; index < length; index++) {
        if (index === length - 1 || Number.isInteger(parseInt(supplierCodeHash[index + 1]))) {
            symbol = supplierCodeHash[index]
        } else {
            symbol = supplierCodeHash[index] + supplierCodeHash[index + 1]
            index++
        }
        if (symbol === '9b') {
            supplierCode += '.'
        } else {
            supplierCode += Object.keys(alphabetMap).find(key => alphabetMap[key] === symbol)
        }
    }
    return supplierCode
}

const arrayEquals = (array1, array2) => {
    const array2Sorted = array2.slice().sort()
    return (
        array1.length === array2.length &&
        array1
            .slice()
            .sort()
            .every((value, index) => value === array2Sorted[index])
    )
}

const dateToUTC = date => addMinutes(date, new Date().getTimezoneOffset())

const UTCToDate = utcDate => addMinutes(utcDate, -new Date().getTimezoneOffset())

function capitalizeTheFirstLetterOfEachWord(words) {
    return words.replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase())
}

export function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
}

function itineraryKey(b2cKey, orderId) {
    return hashStr(b2cKey + orderId)
}

function getServiceTypeIcon(service: {serviceType: string} | null | undefined) {
    switch (service?.serviceType) {
        case 'ACCOMMODATION':
            return 'mdi-office-building-outline'
        case 'FLIGHT':
            return 'mdi-airplane'
        case 'OWNCHARTER':
            return 'mdi-airplane-takeoff'
        case 'CARRENT':
        case 'CAR_RENT':
        case 'CAR':
            return 'mdi-car'
        case 'ACTIVITY':
        case 'EXCURSION':
        case 'EVENT':
            return 'mdi-lightning-bolt-outline'
        case 'TRANSFER':
            return 'mdi-bus'
        case 'EXTRASERVICE':
            return 'mdi-cloud-off-outline'
        case 'OWNEXTRASERVICE':
            return 'mdi-domain-plus'
        case 'PACKAGE_TOUR':
            return 'mdi-gift-outline'
        case 'DYNAMIC_PACKAGE':
            return 'mdi-wallet-travel'
        case 'TRAIN':
            return 'mdi-train'
        case 'INSURANCE':
            return 'mdi-file-document-outline'
        case 'CRUISE':
            return 'mdi-ferry'
        case 'OWNVISA':
            return 'mdi-credit-card-outline'
        case 'BANK_FEE':
            return 'mdi-cash-fast'
        case 'TOUR':
            return 'mdi-transit-detour'
        case 'OTHER':
            return 'mdi-ticket'
        case 'VISA':
            return 'mdi-account-card-outline'
    }
}

const hotelNameToUrl = name => name.toLowerCase().trim().replace(/\s+/g, '-').replace(/\//g, '_')

const hotelAddressToString = (addressObject, serviceDetails = null) => {
    if (!addressObject) return ''
    const {cityName} = addressObject

    let {countryName} = addressObject
    if (!countryName && serviceDetails?.countryName) {
        countryName = serviceDetails?.countryName
    }

    let addressLine = addressObject.addressLine || ''
    if (cityName && !addressLine.includes(cityName)) addressLine += `, ${cityName}`
    if (countryName && !addressLine.includes(countryName)) addressLine += `, ${countryName}`
    return addressLine.startsWith(',') ? addressLine.replace(/^.{2}/, '') : addressLine
}

const PHONE_REGEXP = /^[\d+\-()\s]*$/

export const blobToBase64 = (blob): Promise<string> =>
    new Promise(resolve => {
        const reader = new FileReader()
        reader.readAsDataURL(blob)
        reader.onloadend = () => resolve(reader.result as string)
    })

export const arrayBufferToBase64 = arrayBuffer =>
    btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''))

export const StrToArray = (strData, strDelimiter = ',') => {
    const objPattern = new RegExp(
        '(\\' + strDelimiter + '|\\r?\\n|\\r|^)' + '(?:"([^"]*(?:""[^"]*)*)"|' + '([^"\\' + strDelimiter + '\\r\\n]*))',
        'gi'
    )
    let arrData = [[]]
    let arrMatches = null

    while ((arrMatches = objPattern.exec(strData))) {
        let strMatchedDelimiter = arrMatches[1]
        let strMatchedValue

        if (strMatchedDelimiter.length && strMatchedDelimiter !== strDelimiter) arrData.push([])
        if (arrMatches[2]) strMatchedValue = arrMatches[2].replace(new RegExp('""', 'g'), '"')
        else strMatchedValue = arrMatches[3]

        arrData[arrData.length - 1].push(strMatchedValue)
    }

    return arrData
}

export const thumbnailUrl = (url: string, size: number, fullUrl: Function) => {
    if (url) {
        // resizer does not support webp
        if (url.indexOf('sunhotels') !== -1 || url.indexOf('tboholidays') !== -1 || url.endsWith('.webp')) return url
        return fullUrl(`/thumbnails/${size}/${url}`)
    }
}

export const isBlankItem = item => {
    for (const key in item) {
        if (item[key]) return false
    }
    return true
}

export const formatDateToISO = (date): string => {
    const offset = date.getTimezoneOffset()
    const localDate = new Date(date.getTime() - offset * 60 * 1000)
    return localDate.toISOString().slice(0, 16)
}

export const toBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
    })
}

export function prepareMultiLingualData(name: string | null, lang: string): {[key: string]: string} {
    if (!name) return {}
    let newName: {[key: string]: string} = {}
    newName[lang] = name
    return newName
}

export const isEqual = _isEqual
export const isEmpty = _isEmpty

export const nights = (startDate: string, endDate: string) => differenceInDays(parseISO(endDate), parseISO(startDate))

export const getName = (name): string => {
    if (!name) return ''
    if (typeof name === 'string') return name
    if (name?.en) return name.en
    const firstAvailableKey = Object.keys(name)[0]
    return name[firstAvailableKey]
}

export {
    clone,
    hash,
    hashStr,
    interopDefault,
    timeout,
    later,
    clean,
    matches,
    formatAxiosError,
    downloadData,
    downloadDataAsync,
    getOfferAirportName,
    chunkArray,
    flipArray,
    queryMapper,
    encodeSupplierCode,
    decodeSupplierCode,
    arrayEquals,
    dateToUTC,
    UTCToDate,
    capitalizeTheFirstLetterOfEachWord,
    itineraryKey,
    getServiceTypeIcon,
    hotelNameToUrl,
    hotelAddressToString,
    PHONE_REGEXP,
}
